import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { renderPublicPage, LEGAL_PATHS } from "./components/PublicPages";
import axios from "axios";
import { Toaster } from "./components/ui/toaster";

// Contexts
import { AuthProvider, useAuth } from "./contexts/AuthContext";

// Components
import LandingPage from "./components/LandingPage";
import { Seo } from "./seo/Seo";
import SupportedCitiesPage from "./components/SupportedCitiesPage";
import AuthScreen from "./components/AuthScreen";
import Navbar from "./components/Navbar";
import EnhancedSwipeCards from "./components/EnhancedSwipeCards";
import Matches from "./components/Matches";
import Messages from "./components/Messages";
import Profile from "./components/Profile";
import Settings from "./components/Settings";
import ReceivedTips from "./components/ReceivedTips";
import PersonalityInsights from "./components/PersonalityInsights";

// Footnote Pages

// Admin App
import AdminApp from "./components/AdminApp";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Loading component
const LoadingScreen = () => (
  <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-amber-50 flex items-center justify-center" data-testid="loading-screen">
    <div className="text-center px-6">
      <BrandLogo variant="wings" className="h-44 sm:h-56 w-auto mx-auto mb-6 drop-shadow-xl" sizes="(max-width: 640px) 220px, 280px" priority testId="loading-logo" />
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-500 mx-auto mb-3"></div>
      <p className="text-gray-600">Loading FlameLink...</p>
    </div>
  </div>
);

// Main app content component
import { ResetPasswordScreen, VerifyEmailScreen, VerifyEmailBanner, VerifyEmailGate } from "./components/AccountRecovery";
import { PaymentReturn } from "./components/Payments";
import { installClientErrorReporting } from "./services/errorReporting";
import { useToast } from "./hooks/use-toast";
import GoogleCompleteProfile from "./components/GoogleCompleteProfile";
import { BrandLogo } from "./components/BrandLogo";

const AppContent = () => {
  const { isAuthenticated, loading, user, refreshUser, logout, loginWithGoogleCode } = useAuth();
  const [currentView, setCurrentView] = useState('discover');
  const [showAuth, setShowAuth] = useState(false);
  const [showSupportedCities, setShowSupportedCities] = useState(false);
  const [showLandingPage, setShowLandingPage] = useState(false); // Add this state
  const [chatMatchId, setChatMatchId] = useState(null);
  const [googleOnboarding, setGoogleOnboarding] = useState(null);
  const [googlePending, setGooglePending] = useState(() => new URLSearchParams(window.location.hash.slice(1)).has('google_login'));
  const { toast } = useToast();

  // Google callback returns here with #google_login=<one-time code> (fragment: never sent to the server or logs).
  useEffect(() => {
    const frag = new URLSearchParams(window.location.hash.slice(1));
    const code = frag.get('google_login');
    const err = frag.get('google_error');
    if (!code && !err) return;
    window.history.replaceState({}, '', window.location.pathname);
    if (err) {
      const msgs = { cancelled: 'Google sign-in was cancelled.', email_not_verified: 'Your Google account email is not verified.', not_configured: 'Google sign-in is not configured.', invalid_client: 'Google rejected this app\'s OAuth credentials (invalid_client). The administrator must correct the Google client secret.', exchange_failed: 'Google did not accept the sign-in request. Please try again.', invalid_state: 'This sign-in link expired or was already used. Please try again.', verification_failed: 'Google identity could not be verified. Please try again.' };
      toast({ title: 'Google sign-in failed', description: msgs[err] || 'Please try again.', variant: 'destructive' });
      setShowAuth(true);
      return;
    }
    loginWithGoogleCode(code).then((r) => {
      setGooglePending(false);
      if (!r.success) {
        toast({ title: 'Google sign-in failed', description: r.error, variant: 'destructive' });
        setShowAuth(true);
      } else if (r.kind === 'admin') {
        window.location.replace('/admin');
      } else if (r.kind === 'onboarding') {
        setGoogleOnboarding(r.onboarding);
      } else {
        toast({ title: 'Welcome back!', description: 'Signed in with Google.' });
      }
    });
  }, []); // eslint-disable-line

  const navigate = useNavigate();
  const location = useLocation();
  const handleFootnoteNavigation = (page) => {
    navigate(LEGAL_PATHS[page] || '/');
  };

  const handleBackToHome = () => {
    setShowSupportedCities(false);
    setShowAuth(false);
    setShowLandingPage(false); // Reset landing page state
    navigate('/');
  };

  const handleBackToLanding = () => {
    // User wants to go back to landing page but stay logged in
    setShowLandingPage(true); // Show landing page while remaining logged in
    setShowAuth(false);
    setShowSupportedCities(false);
    navigate('/');
  };

  const handleGetStartedFromLanding = () => {
    if (isAuthenticated) {
      // If user is already logged in, go to dashboard
      setShowLandingPage(false);
    } else {
      // If not logged in, show auth screen
      setShowAuth(true);
    }
  };

  const helloWorldApi = async () => {
    try {
      const response = await axios.get(`${API}/`);
      console.log(response.data.message);
    } catch (e) {
      console.error(e, `errored out requesting / api`);
    }
  };

  useEffect(() => {
    helloWorldApi();
    installClientErrorReporting();
  }, []);

  const params = new URLSearchParams(window.location.search);
  const resetToken = params.get('reset_token');
  const verifyToken = params.get('verify_token');
  const clearTokenParam = () => { window.history.replaceState({}, '', window.location.pathname); window.location.reload(); };

  if (loading || googlePending) {
    return <LoadingScreen />;
  }
  if (googleOnboarding && !isAuthenticated) {
    return <GoogleCompleteProfile onboarding={googleOnboarding} onCancel={() => { setGoogleOnboarding(null); setShowAuth(true); }} />;
  }
  if (resetToken) {
    return <ResetPasswordScreen token={resetToken} onDone={clearTokenParam} />;
  }
  if (verifyToken) {
    return <VerifyEmailScreen token={verifyToken} onDone={clearTokenParam} />;
  }
  const paymentOutcome = window.location.pathname === '/payment/success' ? 'success' : window.location.pathname === '/payment/cancel' ? 'cancel' : null;
  if (paymentOutcome && isAuthenticated) {
    return <PaymentReturn outcome={paymentOutcome} sessionId={params.get('session_id')} onDone={() => { window.history.replaceState({}, '', '/'); window.location.reload(); }} />;
  }

  // Public legal / info pages have real URLs (direct open + refresh safe)
  const publicPage = renderPublicPage(location.pathname, { onBack: handleBackToHome, onGetStarted: () => { navigate('/'); setShowAuth(true); } });
  if (publicPage) return publicPage;

  // Handle authenticated users who want to see landing page
  if (isAuthenticated && showLandingPage) {
    return (
      <LandingPage 
        onGetStarted={handleGetStartedFromLanding}
        onViewCities={() => navigate('/cities')}
        onFootnoteClick={handleFootnoteNavigation}
      />
    );
  }

  if (!isAuthenticated) {
    if (showSupportedCities) {
      return (
        <SupportedCitiesPage 
          onBack={() => setShowSupportedCities(false)}
          onGetStarted={() => {
            setShowSupportedCities(false);
            setShowAuth(true);
          }}
        />
      );
    }
    if (showAuth) {
      return <><Seo path="/login" noindex title="Sign in | FlameLink" description="Sign in or create your FlameLink account." /><AuthScreen /></>;
    }
    return (
      <LandingPage 
        onGetStarted={() => setShowAuth(true)}
        onViewCities={() => navigate('/cities')}
        onFootnoteClick={handleFootnoteNavigation}
      />
    );
  }

  const renderCurrentView = () => {
    switch (currentView) {
      case 'discover':
        return <EnhancedSwipeCards onViewMatches={() => setCurrentView('matches')} onOpenChat={(matchId) => { setChatMatchId(matchId || null); setCurrentView('messages'); }} onUpgrade={() => { setCurrentView('settings'); setTimeout(() => document.getElementById('premium')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150); }} />;
      case 'matches':
        return <Matches onOpenChat={(matchId) => { setChatMatchId(matchId || null); setCurrentView('messages'); }} onUpgrade={() => setCurrentView('settings')} />;
      case 'messages':
        return <Messages key={chatMatchId || 'list'} initialMatchId={chatMatchId} />;
      case 'tips':
        return <ReceivedTips />;
      case 'insights':
        return <PersonalityInsights />;
      case 'profile':
        return <Profile />;
      case 'settings':
        return <Settings onNavigate={setCurrentView} onFootnoteClick={handleFootnoteNavigation} />;
      default:
        return <EnhancedSwipeCards />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-red-50">
      {user?.verification_required ? (
        <VerifyEmailGate user={user} onRefresh={refreshUser} onLogout={logout} />
      ) : (
        <>
      <Seo path="/app" noindex title="FlameLink" description="Your FlameLink account." />
      <VerifyEmailBanner user={user} />
      <Navbar 
        currentView={currentView} 
        setCurrentView={setCurrentView}
        onBackToLanding={handleBackToLanding}
      />
      
      <main className="container mx-auto px-4 pt-6 pb-28 max-w-md">
        {renderCurrentView()}
      </main>
        </>
      )}
      
      <Toaster />
    </div>
  );
};

const App = () => {
  // Check if current path is admin
  const currentPath = window.location.pathname;
  
  if (currentPath.startsWith('/admin')) {
    return <AdminApp />;
  }
  
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="min-h-screen bg-white">
          <AppContent />
          <Toaster />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
};

export default App;